import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Fade,
  Slide,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Badge,
  Tooltip
} from '@mui/material';
import {
  AccessTime,
  ExitToApp,
  LocationOn,
  Coffee,
  PlayArrow,
  Stop,
  CheckCircle,
  History,
  Today,
  Schedule,
  Fingerprint,
  MyLocation,
  LocationOff,
  Work,
  Warning
} from '@mui/icons-material';
import { format, differenceInMinutes, differenceInHours } from 'date-fns';
import api from '../../services/api';
import BiometricAuth from './BiometricAuth';
import './MobileAttendance.css';

const MobileAttendance = ({ user }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockInDialog, setClockInDialog] = useState(false);
  const [clockOutDialog, setClockOutDialog] = useState(false);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [onBreak, setOnBreak] = useState(false);
  const [recentHistory, setRecentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // New state variables for advanced features
  const [showBiometric, setShowBiometric] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [shift, setShift] = useState(null);
  const [allowedLocations, setAllowedLocations] = useState([]);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [offlineMode, setOfflineMode] = useState(!navigator.onLine);

  const locations = [
    'Recording Studio',
    'Photo Studio', 
    'Outside Recording',
    'Office',
    'Remote'
  ];

  useEffect(() => {
    fetchTodayAttendance();
    fetchRecentHistory();
    fetchTodayShift();
    fetchAllowedLocations();
    checkBiometricRegistration();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    // Refresh attendance every minute
    const refreshTimer = setInterval(() => {
      fetchTodayAttendance();
    }, 60000);
    
    // Check online status
    const handleOnline = () => setOfflineMode(false);
    const handleOffline = () => setOfflineMode(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Request location permission on mount
    if ('geolocation' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          setLocationEnabled(true);
          getCurrentLocation();
        }
      });
    }
    
    return () => {
      clearInterval(timer);
      clearInterval(refreshTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      const response = await api.get('/attendance/today');
      setTodayAttendance(response.data);
      
      // Check if on break
      if (response.data && response.data.break_start_time && !response.data.break_end_time) {
        setOnBreak(true);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      if (offlineMode) {
        // Load from local storage if offline
        const cached = localStorage.getItem('todayAttendance');
        if (cached) setTodayAttendance(JSON.parse(cached));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentHistory = async () => {
    try {
      const response = await api.get('/attendance/report', {
        params: {
          start_date: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          end_date: format(new Date(), 'yyyy-MM-dd')
        }
      });
      setRecentHistory(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchTodayShift = async () => {
    try {
      const response = await api.get('/attendance/shifts', {
        params: {
          start_date: format(new Date(), 'yyyy-MM-dd'),
          end_date: format(new Date(), 'yyyy-MM-dd'),
          user_id: user.id
        }
      });
      if (response.data.length > 0) {
        setShift(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching shift:', error);
    }
  };

  const fetchAllowedLocations = async () => {
    try {
      const response = await api.get('/attendance/allowed-locations');
      setAllowedLocations(response.data);
    } catch (error) {
      console.error('Error fetching allowed locations:', error);
    }
  };

  const checkBiometricRegistration = async () => {
    try {
      const deviceId = localStorage.getItem('deviceId');
      if (deviceId && 'credentials' in navigator) {
        setBiometricEnabled(true);
      }
    } catch (error) {
      console.error('Error checking biometric:', error);
    }
  };

  // Geolocation support
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(location);
          setLocationAccuracy(position.coords.accuracy);
          
          // Store in localStorage for offline use
          localStorage.setItem('lastLatitude', location.latitude);
          localStorage.setItem('lastLongitude', location.longitude);
          
          resolve(location);
        },
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Enhanced clock in with location
  const handleClockIn = async () => {
    try {
      let locationData = {};
      
      // Try to get current location if enabled
      if (locationEnabled) {
        try {
          locationData = await getCurrentLocation();
        } catch (locationError) {
          console.error('Location error:', locationError);
          showSnackbar('Location unavailable, continuing without location', 'warning');
        }
      }
      
      const clockInData = {
        location,
        notes,
        ...locationData
      };
      
      if (offlineMode) {
        // Store offline
        const offlineData = JSON.parse(localStorage.getItem('offlineClockIns') || '[]');
        offlineData.push({
          ...clockInData,
          timestamp: new Date().toISOString(),
          synced: false
        });
        localStorage.setItem('offlineClockIns', JSON.stringify(offlineData));
        
        // Update local state
        const mockAttendance = {
          clock_in: new Date().toISOString(),
          location,
          location_verified: false,
          offline: true
        };
        setTodayAttendance(mockAttendance);
        localStorage.setItem('todayAttendance', JSON.stringify(mockAttendance));
        
        showSnackbar('Clocked in offline. Will sync when online.', 'info');
      } else {
        await api.post('/attendance/clock-in-location', clockInData);
        showSnackbar('Clocked in successfully!', 'success');
      }
      
      setClockInDialog(false);
      setLocation('');
      fetchTodayAttendance();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error clocking in', 'error');
    }
  };

  const handleClockOut = async () => {
    try {
      if (offlineMode) {
        // Handle offline clock out
        const attendance = { ...todayAttendance, clock_out: new Date().toISOString() };
        setTodayAttendance(attendance);
        localStorage.setItem('todayAttendance', JSON.stringify(attendance));
        
        const offlineData = JSON.parse(localStorage.getItem('offlineClockOuts') || '[]');
        offlineData.push({
          notes,
          timestamp: new Date().toISOString(),
          synced: false
        });
        localStorage.setItem('offlineClockOuts', JSON.stringify(offlineData));
        
        showSnackbar('Clocked out offline. Will sync when online.', 'info');
      } else {
        await api.post('/attendance/clock-out', { notes });
        showSnackbar('Clocked out successfully!', 'success');
      }
      
      setClockOutDialog(false);
      setNotes('');
      fetchTodayAttendance();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error clocking out', 'error');
    }
  };

  const handleBreak = async (action) => {
    try {
      await api.post(`/attendance/break/${action}`);
      setOnBreak(action === 'start');
      fetchTodayAttendance();
      showSnackbar(`Break ${action}ed successfully!`, 'success');
    } catch (error) {
      showSnackbar(error.response?.data?.message || `Error ${action}ing break`, 'error');
    }
  };

  const handleBiometricSuccess = (data) => {
    setShowBiometric(false);
    fetchTodayAttendance();
    showSnackbar('Clocked in successfully with biometric!', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getWorkDuration = () => {
    if (!todayAttendance?.clock_in) return '0h 0m';
    
    const clockIn = new Date(todayAttendance.clock_in);
    const clockOut = todayAttendance.clock_out ? new Date(todayAttendance.clock_out) : currentTime;
    
    const totalMinutes = differenceInMinutes(clockOut, clockIn);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  const getBreakDuration = () => {
    if (!todayAttendance?.break_start_time) return '0m';
    
    const breakStart = new Date(todayAttendance.break_start_time);
    const breakEnd = todayAttendance.break_end_time ? new Date(todayAttendance.break_end_time) : currentTime;
    
    const minutes = differenceInMinutes(breakEnd, breakStart);
    return `${minutes}m`;
  };

  const getLocationAccuracyColor = () => {
    if (!locationAccuracy) return 'default';
    if (locationAccuracy <= 10) return 'success';
    if (locationAccuracy <= 50) return 'warning';
    return 'error';
  };

  const isClockedIn = todayAttendance && todayAttendance.clock_in && !todayAttendance.clock_out;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="mobile-attendance-container" sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      pb: 3 
    }}>
      {/* Offline Banner */}
      {offlineMode && (
        <Box className="offline-banner">
          <Warning fontSize="small" sx={{ mr: 1 }} />
          You're offline. Your data will sync when connection is restored.
        </Box>
      )}

      {/* Header */}
      <Paper elevation={0} sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 2, 
        borderRadius: '0 0 24px 24px' 
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ mr: 2, bgcolor: 'primary.dark' }}>
              {user.full_name[0]}
            </Avatar>
            <Box>
              <Typography variant="h6">{user.full_name}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {user.roles.join(', ')}
              </Typography>
            </Box>
          </Box>
          <Box>
            {locationEnabled && (
              <Tooltip title={`Location accuracy: ${locationAccuracy?.toFixed(0)}m`}>
                <IconButton color="inherit" size="small">
                  <Badge 
                    color={getLocationAccuracyColor()} 
                    variant="dot"
                  >
                    <MyLocation />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            <IconButton color="inherit" onClick={() => setShowHistory(!showHistory)}>
              <History />
            </IconButton>
          </Box>
        </Box>
        
        {/* Time Display */}
        <Box textAlign="center" py={3}>
          <Typography variant="h3" fontWeight="bold" className="mobile-clock-display">
            {format(currentTime, 'HH:mm:ss')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>

        {/* Shift Information */}
        {shift && (
          <Box textAlign="center" pb={2}>
            <Chip
              icon={<Work />}
              label={`Shift: ${shift.shift_name} (${shift.actual_start} - ${shift.actual_end})`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
            />
          </Box>
        )}
      </Paper>

      <Container maxWidth="sm" sx={{ mt: -4 }}>
        {/* Status Card */}
        <Fade in timeout={500}>
          <Card sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: isClockedIn ? 'success.light' : 'background.paper',
            className: isClockedIn ? 'pulse-animation' : ''
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Today's Status</Typography>
                <Chip
                  icon={isClockedIn ? <CheckCircle /> : <Schedule />}
                  label={isClockedIn ? 'Clocked In' : 'Not Clocked In'}
                  color={isClockedIn ? 'success' : 'default'}
                />
              </Box>

              {isClockedIn && (
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Clock In Time
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(todayAttendance.clock_in), 'HH:mm')}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {getWorkDuration()}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">
                        {todayAttendance.location}
                      </Typography>
                      {todayAttendance.location_verified && (
                        <CheckCircle fontSize="small" color="success" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  </Box>
                  {onBreak && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        On Break
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        {getBreakDuration()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {todayAttendance?.clock_out && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  You clocked out at {format(new Date(todayAttendance.clock_out), 'HH:mm')}
                </Alert>
              )}

              {todayAttendance?.offline && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This record was created offline and is pending sync
                </Alert>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Action Buttons */}
        <Box mb={3}>
          {!isClockedIn && !todayAttendance?.clock_out && (
            <Box>
              <Slide direction="up" in timeout={700}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={() => setClockInDialog(true)}
                  sx={{ 
                    py: 2, 
                    borderRadius: 2,
                    fontSize: '1.2rem',
                    boxShadow: 3,
                    mb: 2
                  }}
                >
                  Clock In
                </Button>
              </Slide>
              
              {/* Biometric authentication option */}
              {biometricEnabled && (
                <Slide direction="up" in timeout={900}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<Fingerprint />}
                    onClick={() => setShowBiometric(true)}
                    sx={{ 
                      py: 1.5, 
                      borderRadius: 2,
                      borderWidth: 2
                    }}
                  >
                    Clock In with Biometric
                  </Button>
                </Slide>
              )}
            </Box>
          )}

          {isClockedIn && (
            <Box>
              <Slide direction="up" in timeout={700}>
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<Stop />}
                    onClick={() => setClockOutDialog(true)}
                    sx={{ 
                      py: 2, 
                      mb: 2,
                      borderRadius: 2,
                      fontSize: '1.2rem',
                      boxShadow: 3
                    }}
                  >
                    Clock Out
                  </Button>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<Coffee />}
                    onClick={() => handleBreak(onBreak ? 'end' : 'start')}
                    color={onBreak ? 'warning' : 'primary'}
                    sx={{ 
                      py: 1.5, 
                      borderRadius: 2,
                      borderWidth: 2
                    }}
                  >
                    {onBreak ? 'End Break' : 'Take Break'}
                  </Button>
                </Box>
              </Slide>
            </Box>
          )}
        </Box>

        {/* Recent History */}
        {showHistory && (
          <Fade in timeout={500}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Attendance
                </Typography>
                <List>
                  {recentHistory.map((record, index) => (
                    <React.Fragment key={record.id}>
                      <ListItem disablePadding>
                        <ListItemText
                          primary={format(new Date(record.date), 'EEEE, MMM d')}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                {record.clock_in ? format(new Date(record.clock_in), 'HH:mm') : '-'} 
                                {' → '}
                                {record.clock_out ? format(new Date(record.clock_out), 'HH:mm') : 'Active'}
                              </Typography>
                              {record.total_hours && (
                                <Typography variant="caption" color="primary">
                                  {record.total_hours.toFixed(1)} hours
                                </Typography>
                              )}
                              {record.location_verified && (
                                <Chip 
                                  label="Location Verified" 
                                  size="small" 
                                  color="success"
                                  sx={{ ml: 1, height: 16 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentHistory.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Fade>
        )}
      </Container>

      {/* Clock In Dialog */}
      <Dialog 
        open={clockInDialog} 
        onClose={() => setClockInDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Clock In</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            margin="normal"
            required
            autoFocus
          >
            {locations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                <Box display="flex" alignItems="center">
                  <LocationOn fontSize="small" sx={{ mr: 1 }} />
                  {loc}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={locationEnabled}
                onChange={(e) => {
                  setLocationEnabled(e.target.checked);
                  if (e.target.checked) {
                    getCurrentLocation();
                  }
                }}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center">
                Enable location tracking
                {currentLocation && (
                  <Chip 
                    label={`±${locationAccuracy?.toFixed(0)}m`}
                    size="small"
                    color={getLocationAccuracyColor()}
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            }
            sx={{ mt: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            placeholder="Any notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockInDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleClockIn} 
            variant="contained" 
            disabled={!location}
          >
            Clock In
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clock Out Dialog */}
      <Dialog 
        open={clockOutDialog} 
        onClose={() => setClockOutDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Clock Out</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You worked {getWorkDuration()} today
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            placeholder="Any notes about today's work..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockOutDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleClockOut} 
            variant="contained" 
            color="error"
          >
            Clock Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Biometric Authentication Dialog */}
      <BiometricAuth
        open={showBiometric}
        onClose={() => setShowBiometric(false)}
        onSuccess={handleBiometricSuccess}
        action="clock-in"
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileAttendance;
